/**
 * @file iframe 通讯
 */
import { URL } from '@/utils/dom'
import { Tracker } from '@/utils/event-lib'

// 检查通讯连通性，带2次重试
const checkConnect = (url: string) => {
  if (!window.fetch) {
    return Promise.resolve(false)
  }
  let retryCount = 0
  const RETRY_TIMES = 2

  const withRetry = (
    url: string,
    options = {}
  ): Promise<boolean> =>
    fetch(url, options)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`retry ${retryCount + 1}`)
        }
        return true
      })
      .catch(() => {
        retryCount++
        if (retryCount === RETRY_TIMES) {
          return false
        }
        return withRetry(url, options)
      })

  return withRetry(url, {
    method: 'post',
  })
}

interface iMessage {
  errorType?: string
  errorMsg?: string
  [key: string]: any
}

export class Channel {
  constructor() {
    this.setUseFrame()
    this.checkCompatibility().then((checkList) => {
      if (checkList.length === 0) {
        return
      }
      const value = checkList.every((value) => value)
      if (value === false) {
        // 清理和派发结果
        this.abort({
          errorType: 'check_compatibility_fail',
          errorMsg: checkList.join(','),
        })
      }
    })
    this.bind = this.bind.bind(this)
    this.onPopstate = this.onPopstate.bind(this)
    this.positive(() => {
      window.addEventListener('popstate', this.onPopstate)
      window.addEventListener('message', this.bind)
    })
  }

  private onPopstate(event: PopStateEvent) {
    if (event.state === null) {
      this.dispatch('preload_page_close')
    }
  }

  resetStyle(toggle = true) {
    try {
      const el = document.getElementById('app')
      if (!el) {
        return
      }
      el.style.height = toggle ? '100vh' : ''
      el.style.overflow = toggle ? 'scroll' : ''
    } catch (e) {
      console.log('[Channel-lib]', 'Rest body styles')
    }
  }

  /**
   * 是否使用iframe通讯，外部使用
   */
  get isUseFrame() {
    return this.useFrame
  }

  // 是否iframe通讯
  private useFrame = false

  /**
   * 重制 iframe通讯锁 value
   * @param val 可选，不传以URL参数为准
   * @returns boolean
   */
  private setUseFrame(val?: boolean) {
    if (typeof val === 'boolean') {
      this.useFrame = val
      return
    }
    this.useFrame = +URL.get('fromIframe') === 1
  }

  /**
   * 当前页面非iframe打开时执行
   */
  fallback(cb?: () => void) {
    this.positive(void 0, cb)
  }

  /**
   * 清理
   */
  abort(msg?: iMessage) {
    console.log('[Channel-lib]', 'abort', msg)
    // 派发失败事件
    this.dispatch('preload_page_fail', msg)
    // 关闭使用iframe通讯
    this.setUseFrame(false)
    // 清理事件
    window.removeEventListener('message', this.bind)
    window.removeEventListener('popstate', this.onPopstate)
    // 样式回归
    this.resetStyle(false)
    // 终断上报
    Tracker.trackError(
      'tech_freya_kyc_iframe_error',
      msg || {}
    )
  }

  /**
   * 正向流程调用
   */
  positive(cb?: () => any, fallback?: () => any) {
    if (this.useFrame) {
      if (typeof cb === 'function') {
        return cb()
      }
      return
    }
    if (typeof fallback === 'function') {
      return fallback()
    }
  }

  /**
   * 检查omega/passport等是否可用
   */
  private checkCompatibility(): Promise<boolean[]> {
    return this.positive(
      () =>
        Promise.all([
          checkConnect(
            `https://${window.__INIT_STATE__.omega.uploadHost}/api/web/stat?e=`
          ),
          checkConnect(
            `https://api-sec.didiglobal.com/sign/v1/wsgsigsession/signinit`
          ),
        ]),
      () => Promise.resolve([])
    )
  }

  private WHITE_LIST = [
    '.didiglobal.com',
    '.didipay.com',
    '.didifinance.com',
    '.xiaojukeji.com',
    // 'localhost:9122',
  ]

  private checkWhitelist(origin: string) {
    if (typeof origin === 'string') {
      return this.WHITE_LIST.some((domain) =>
        origin.endsWith(domain)
      )
    }
    return false
  }

  /**
   * 通讯回调
   */
  private bind(event: MessageEvent) {
    if (!this.checkWhitelist(event.origin)) {
      return console.log(
        '[Channel-lib]',
        `Current origin ${event.origin} not allow.`
      )
    }
    console.log(
      '[Channel-lib]',
      'Event received: ',
      event?.type,
      event?.data
    )
    this.emit(event?.data?.type, event.data)
  }

  private events = new Map()
  /**
   * 订阅父级的事件
   */
  subscribe(event: string, cb: (data?: unknown) => void) {
    const refs = this.events.get(event) || []
    refs.push(cb)
    this.events.set(event, refs)
  }

  private emit(event: string, data: unknown) {
    const cbs = this.events.get(event)
    if (!Array.isArray(cbs)) {
      return
    }
    cbs.forEach((cb: unknown) => {
      if (typeof cb === 'function') {
        cb(data)
      }
    })
  }

  /**
   * 通讯函数
   */
  dispatch(
    type:
      | 'preload_page_onload_success'
      | 'preload_page_receive_login_success'
      | 'preload_page_render_success'
      | 'preload_page_close'
      | 'preload_page_open_link'
      | 'preload_page_fail',
    data?: iMessage
  ) {
    this.positive(() => {
      // const origin = window.parent.origin
      // if (!this.checkWhitelist(origin)) {
      //   return console.log(
      //     '[Chanel-lib]',
      //     'Parent origin not allow!'
      //   )
      // }
      console.log(
        '[Channel-lib]',
        'Event dispatch',
        type,
        data
      )
      window.parent.postMessage(
        {
          type,
          data,
        },
        '*'
        // origin
      )
    })
  }
}

export default new Channel()
